import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const MarqueeDataApi = createApi({
  reducerPath: "marqueeDataApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["MarqueeData"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // ✅ 1. Get all marquee data with pagination, search, and sorting
    getMarqueeData: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "display_order",
        order = "asc",
      } = {}) => ({
        url: "/marqueeData",
        params: { page, limit, search, sortBy, order },
      }),
      /* Normalize API response */
      transformResponse: (response) => ({
        marquees: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),
      /* Correct tag strategy */
      providesTags: (result) =>
        result?.marquees?.length
          ? [
              ...result.marquees.map(({ id }) => ({
                type: "MarqueeData",
                id: Number(id),
              })),
              { type: "MarqueeData", id: "LIST" },
            ]
          : [{ type: "MarqueeData", id: "LIST" }],
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // ✅ 2. Get single marquee data by ID
    getMarqueeDataById: builder.query({
      query: (id) => `/marqueeData/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "MarqueeData", id }],
    }),

    // ✅ 3. Create new marquee entry
    createMarqueeData: builder.mutation({
      query: (newData) => ({
        url: "/marqueeData",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: [{ type: "MarqueeData", id: "LIST" }],
    }),

    // ✅ 4. Update marquee entry by ID
    updateMarqueeData: builder.mutation({
      query: ({ id, ...updatedData }) => ({
        url: `/marqueeData/${id}`,
        method: "PUT",
        body: updatedData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "MarqueeData", id },
        { type: "MarqueeData", id: "LIST" },
      ],
    }),

    // ✅ 5. Delete marquee entry by ID
    deleteMarqueeData: builder.mutation({
      query: (id) => ({
        url: `/marqueeData/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "MarqueeData", id: "LIST" }],
    }),

    // ✅ 6. Toggle marquee status (enable/disable)
    toggleMarqueeStatus: builder.mutation({
      query: (id) => ({
        url: `/marqueeData/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "MarqueeData", id },
        { type: "MarqueeData", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetMarqueeDataQuery,
  useGetMarqueeDataByIdQuery,
  useCreateMarqueeDataMutation,
  useUpdateMarqueeDataMutation,
  useDeleteMarqueeDataMutation,
  useToggleMarqueeStatusMutation,
} = MarqueeDataApi;
